import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import io, { type Socket } from "socket.io-client";

import { SocketUrl } from "../../config/SocketUrl";
import { useBoundStore } from "../../store";

// Stable per-session browser identifier — mirrors Constants.DEVICE_ID on mobile
const getDeviceId = (): string => {
  const key = "chat_device_id";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
};

export const DEVICE_ID = getDeviceId();

// ── Message polling payload shape (mirrors mobile MessagePolling type) ────────
export type MessagePollingArgs = {
  chatroomId: string;
  sentTime: string;
  messageId: string;
  message: string;
  chatThreadType?: string;
  chatroomArchived?: boolean;
  userTypeMessages?: string;
  isDeleted?: boolean;
  hasAttachment?: boolean;
};

export type MessagePolling = {
  args: MessagePollingArgs;
};

// ── Socket callback / payload types ──────────────────────────────────────────

type SocketEventCallback = (...args: unknown[]) => void;

export interface CitusUserInfo {
  userId: number;
  displayName: string;
  roleId: number;
}

export interface SocketRenamePayload {
  chatHeading: string;
}

interface CitusHandoffData {
  userInfo: CitusUserInfo;
  configData: unknown;
}

// ── Named subscriber helpers — pure functions, mirror mobile SocketProvider ───

export const subscribeCitusUserInfoHandOffEvent = <T = unknown>(socket: Socket, callback: (data: T) => void) => {
  socket.off(SocketUrl.userInformationHandOff).on(SocketUrl.userInformationHandOff, callback as SocketEventCallback);
};

export const subscribeLastUserActivityEvent = <T = unknown>(socket: Socket, callback: (data: T) => void) => {
  socket.off(SocketUrl.lastUserActivity).on(SocketUrl.lastUserActivity, callback as SocketEventCallback);
};

export const subscribeTypingEvent = <T = unknown>(socket: Socket, callback: (data: T) => void) => {
  socket.off(SocketUrl.userTyping).on(SocketUrl.userTyping, callback as SocketEventCallback);
};

export const subscribeUpdateUserMessageReadStatusEvent = <T = unknown>(socket: Socket, callback: (data: T) => void) => {
  socket.off(SocketUrl.updateUserMessageReadStatus).on(SocketUrl.updateUserMessageReadStatus, callback as SocketEventCallback);
};

export const subscribeReceivedMessageEvent = <T = unknown, U = unknown>(socket: Socket, callback: (data: T, extra: U) => void) => {
  // userMessage can fire for any room — do NOT .off() here to avoid deregistering other room handlers
  socket.on(SocketUrl.userMessage, callback as SocketEventCallback);
};

export const subscribeUpdateMessageIdEvent = <T = unknown>(socket: Socket, callback: (data: T) => void) => {
  socket.off(SocketUrl.updateUserMessage).on(SocketUrl.updateUserMessage, callback as SocketEventCallback);
};

export const subscribeSocketConnectEvent = <T = unknown>(socket: Socket, callback: (data: T) => void) => {
  socket.on(SocketUrl.connect, callback as SocketEventCallback);
};

export const subscribeUpdateRenamegroupchat = <T = unknown>(socket: Socket, callback: (data: T) => void) => {
  socket.off(SocketUrl.roomNameChangeNotification).on(SocketUrl.roomNameChangeNotification, callback as SocketEventCallback);
};

export const subscribeUpdateChatMessageDeleteStatusEvent = <T = unknown>(socket: Socket, callback: (data: T) => void) => {
  socket.off(SocketUrl.updateChatMessageDeleteStatus).on(SocketUrl.updateChatMessageDeleteStatus, callback as SocketEventCallback);
};

// ── Named emitter helpers — pure functions, mirror mobile SocketProvider ──────

export const emitUserJoinToRoomEvent = <T = unknown>(socket: Socket, payload: Record<string, unknown>, callback: (data: T) => void) => {
  socket.emit(SocketUrl.userJoinToChatRoom, payload, callback as SocketEventCallback);
};

export const emitUserTypingEvent = (socket: Socket, payload: string) => {
  socket.emit(SocketUrl.typing, payload);
};

export const emitUserSendMessage = (socket: Socket, payload: object) => {
  console.log(`[Socket] ${SocketUrl.userSendMessage} emitted`, payload);
  socket.emit(SocketUrl.userSendMessage, payload);
};

export const emitUserJoinToAppEvent = <T = unknown>(socket: Socket, payload: Record<string, unknown>, callback: (data: T) => void) => {
  socket.emit(SocketUrl.userJoinToApp, payload, callback as SocketEventCallback);
};

export const emitLeaveChatRoomEvent = <T = unknown>(socket: Socket, payload: unknown, callback: (data: T) => void) => {
  socket.emit(SocketUrl.leaveChatRoom, payload, callback as SocketEventCallback);
};

export const emitMessagePollingReceivedAckEvent = (socket: Socket, payload: object) => {
  console.log(`[Socket] ${SocketUrl.messagePollingReceivedAck} emitted`, payload);
  socket.emit(SocketUrl.messagePollingReceivedAck, payload);
};

export const emitSocketDisconnectEvent = (socket: Socket) => {
  console.log(`[Socket] ${SocketUrl.disconnect} emitted`);
  socket.emit(SocketUrl.disconnect);
};

export const emitUpdateUserTokenEvent = <T = unknown>(socket: Socket, payload: object, callback: (data: T) => void) => {
  console.log(`[Socket] ${SocketUrl.updateUserToken} emitted`);
  socket.emit(SocketUrl.updateUserToken, payload, callback as SocketEventCallback);
};

// ── Context types ─────────────────────────────────────────────────────────────

interface SocketReconnection {
  reconnectData: unknown;
  time: number;
}

interface SocketContextValue {
  socket: Socket | null;
  citusUserInfo: CitusUserInfo | null;
  citusConfigData: unknown;
  messagePollingData: MessagePolling;
  socketReconnection: SocketReconnection;
  closeSocket: () => void;
}

interface SocketProviderProps {
  children: React.ReactNode;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  citusUserInfo: null,
  citusConfigData: null,
  messagePollingData: {} as MessagePolling,
  socketReconnection: { reconnectData: null, time: 0 },
  closeSocket: () => {},
});

export const useSocket = () => useContext(SocketContext);

// ── Provider ──────────────────────────────────────────────────────────────────

const SocketProvider = ({ children }: SocketProviderProps) => {
  const accessToken = useBoundStore((state) => state.shared.accessToken);

  const [socket, setSocket] = useState<Socket | null>(null);
  // Holds the socket instance between io() and handoff — prevents StrictMode double-invoke from creating two connections
  const pendingSocketRef = useRef<Socket | null>(null);
  // Ref so the accessToken effect reads current socket without depending on socket state
  const socketRef = useRef<Socket | null>(null);
  const [userInfo, setUserInfo] = useState<CitusUserInfo | null>(null);
  const [configData, setConfigData] = useState<unknown>(null);
  const [messagePollingData, setMessagePollingData] = useState<MessagePolling>({} as MessagePolling);
  const [socketReconnection, setSocketReconnection] = useState<SocketReconnection>({
    reconnectData: null,
    time: 0,
  });

  const subscribeMessagePollingEvent = (_socket: Socket) => {
    _socket.off(SocketUrl.messagePolling).on(SocketUrl.messagePolling, (data: MessagePolling) => {
      setMessagePollingData(data);
    });
  };

  const subscribeReconnectConnectionEvent = (_socket: Socket, userInfoData: CitusUserInfo, configDataValue: unknown) => {
    _socket.off(SocketUrl.reconnect).on(SocketUrl.reconnect, (reconnectData: unknown) => {
      // Use params passed at registration time — closed-over React state is still null here
      emitUserJoinToAppEvent(
        _socket,
        { userInfo: userInfoData, deviceUUID: DEVICE_ID, configData: configDataValue },
        (data: unknown) => {
          console.log(`[Socket] ${SocketUrl.userJoinToApp} after reconnect`, data);
        },
      );
      setSocketReconnection({ reconnectData, time: Date.now() });
    });
  };

  const updateSocket = useCallback((token: string) => {
    if (pendingSocketRef.current) return; // already connecting — skip
    const socketOptions = {
      secure: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      transports: ["websocket"],
      auth: { token },
      query: { clientId: process.env.SOCKET_CLIENT_ID ?? "" },
    };

    const newSocket = io(process.env.SOCKET_URL ?? "", socketOptions);
    pendingSocketRef.current = newSocket; // set immediately so re-invoked effects see it
    newSocket.connect();

    subscribeCitusUserInfoHandOffEvent(newSocket, (data: unknown) => {
      const { userInfo: handoffUserInfo, configData: handoffConfigData } = data as CitusHandoffData;
      console.log(`[Socket] ${SocketUrl.userInformationHandOff} received`, data);
      pendingSocketRef.current = null; // handoff complete — socket is now in state
      setSocket(newSocket);
      setUserInfo(handoffUserInfo);
      setConfigData(handoffConfigData);

      subscribeMessagePollingEvent(newSocket);
      subscribeReconnectConnectionEvent(newSocket, handoffUserInfo, handoffConfigData);

      emitUserJoinToAppEvent(
        newSocket,
        { userInfo: handoffUserInfo, deviceUUID: DEVICE_ID, configData: handoffConfigData },
        (res: unknown) => {
          console.log(`[Socket] ${SocketUrl.userJoinToApp} response`, res);
        },
      );
    });
  }, []);

  const closeSocket = () => {
    if (socket) {
      emitSocketDisconnectEvent(socket);
      socket.disconnect();
      setSocket(null);
    }
  };

  // Keep socketRef in sync so the accessToken effect always reads the latest value
  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  useEffect(() => {
    if (!accessToken) return;
    if (socketRef.current === null && !pendingSocketRef.current) {
      updateSocket(accessToken);
    } else if (socketRef.current !== null) {
      // Server doesn't support mid-session token updates — full reconnect with new token
      const oldSocket = socketRef.current;
      emitSocketDisconnectEvent(oldSocket);
      oldSocket.disconnect();
      pendingSocketRef.current = null;
      updateSocket(accessToken);
      // defer setState to satisfy react-hooks/set-state-in-effect; functional form guards
      // against a race where the new socket's handoff fires before this timeout runs
      setTimeout(() => setSocket(prev => prev === oldSocket ? null : prev), 0);
    }
    return () => {
      // StrictMode cleanup: disconnect the in-flight socket so the second mount starts fresh
      if (pendingSocketRef.current && socketRef.current === null) {
        pendingSocketRef.current.disconnect();
        pendingSocketRef.current = null;
      }
    };
  }, [accessToken, updateSocket]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        citusUserInfo: userInfo,
        citusConfigData: configData,
        messagePollingData,
        socketReconnection,
        closeSocket,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export { SocketProvider };
